/**
 * AgentViolationReportForm — used by site agents (and any staff role) to
 * file one of the 6 NECS-standard tenant violations directly from the
 * field. Uploads at least one required photo, geo/context info, and
 * posts to /api/violations. Backend auto-notifies the property manager
 * and (via a companion tenant notification) the resident.
 */
import { useEffect, useState } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Camera, AlertTriangle, X, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { NECS_VIOLATION_POLICY, NECS_VIOLATION_BY_KEY } from '../../lib/necsViolationPolicy';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AgentViolationReportForm({ properties = [] }) {
  const [form, setForm] = useState({
    violation_type: '',
    property_id: '',
    unit_number: '',
    building_number: '',
    description: '',
    photos: [],
  });
  const [saving, setSaving] = useState(false);
  const [myViolations, setMyViolations] = useState([]);
  // Iter-89 · Gemini Vision auto-suggest state. Populated after the first
  // photo is added; agent can accept (one tap) or override via the dropdown.
  const [aiSuggestion, setAiSuggestion] = useState(null);   // { key, label, confidence, rationale, low_confidence }
  const [aiLoading, setAiLoading] = useState(false);
  // Iter-91 · Low-confidence gate state — 2-word note the agent must
  // provide before the category field unlocks.
  const [lowConfNote, setLowConfNote] = useState('');
  const [lowConfSubmitted, setLowConfSubmitted] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/api/violations`, { credentials: 'include' });
        if (r.ok) setMyViolations(await r.json());
      } catch (_e) { /* noop */ }
    })();
  }, []);

  const addPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      // Iter-89 · Only auto-suggest off the FIRST photo to keep calls cheap
      // and predictable. Agent already anchored to the classification.
      const shouldSuggest = form.photos.length === 0;
      setForm((f) => ({ ...f, photos: [...f.photos, { data: dataUrl, name: file.name }] }));
      if (shouldSuggest) runAiSuggest(dataUrl);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  // Iter-89 · Gemini Vision auto-classify — sends the raw base64 payload
  // (strips the data:image/…;base64, prefix) to /api/violations/analyze-image.
  const runAiSuggest = async (dataUrl) => {
    try {
      setAiLoading(true);
      setAiSuggestion(null);
      const base64 = (dataUrl || '').replace(/^data:image\/[a-zA-Z]+;base64,/, '');
      const res = await fetch(`${API}/api/violations/analyze-image`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ image_data: base64 }),
      });
      if (!res.ok) return;
      const data = await res.json();
      const key = data.violation_key;
      if (!key || !NECS_VIOLATION_BY_KEY[key]) return;
      const conf = typeof data.confidence === 'number' ? data.confidence : 0.5;
      const lowConf = conf < 0.40;
      setAiSuggestion({
        key,
        label: data.label || NECS_VIOLATION_BY_KEY[key].label,
        confidence: conf,
        rationale: data.rationale || '',
        low_confidence: lowConf,
      });
      // Iter-91 · Low-confidence gate. When Gemini isn't sure (<40%) we
      // do NOT pre-populate the dropdown — the agent must first describe
      // what they see in 2 words. That note doubles as training data.
      if (lowConf) {
        setLowConfNote('');
        setLowConfSubmitted(false);
      } else {
        // Confident suggestion — auto-select ONLY if the agent hasn't picked already.
        setForm((f) => (f.violation_type ? f : { ...f, violation_type: key }));
      }
    } catch {
      /* silent — form remains fully manual */
    } finally {
      setAiLoading(false);
    }
  };

  const acceptSuggestion = () => {
    if (!aiSuggestion) return;
    setForm((f) => ({ ...f, violation_type: aiSuggestion.key }));
    toast.success(`Accepted AI suggestion · ${aiSuggestion.label}`);
  };

  const removePhoto = (idx) => {
    setForm((f) => ({ ...f, photos: f.photos.filter((_, i) => i !== idx) }));
  };

  const submit = async () => {
    if (!form.violation_type) return toast.error('Pick a violation type');
    if (!form.property_id) return toast.error('Select a property');
    if (!form.unit_number.trim()) return toast.error('Unit number is required');
    if (form.photos.length === 0) return toast.error('At least one photo is required for evidence');
    // Iter-91 · If Gemini flagged low confidence, block submit until the
    // agent has provided their 2-word note. This is enforced client-side
    // AND persisted server-side for retraining.
    if (aiSuggestion?.low_confidence && !lowConfSubmitted) {
      return toast.error('Please describe what you see in 2 words before submitting');
    }

    setSaving(true);
    try {
      const res = await fetch(`${API}/api/violations`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          property_id: form.property_id,
          unit_number: form.unit_number.trim(),
          building_number: form.building_number.trim() || undefined,
          violation_type: form.violation_type,
          description: form.description.trim(),
          photos: form.photos,
        }),
      });
      const data = await res.json();
      if (!res.ok) return toast.error(data.detail || 'Submit failed');
      // Iter-89/91 · Fire-and-forget log of accept/override for AI accuracy tracking.
      if (aiSuggestion) {
        fetch(`${API}/api/violations/ai-suggestion-log`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            suggested_key: aiSuggestion.key,
            chosen_key: form.violation_type,
            confidence: aiSuggestion.confidence,
            low_conf_note: lowConfSubmitted ? lowConfNote.trim() : null,
          }),
        }).catch(() => { /* non-blocking */ });
      }
      toast.success('Violation reported — Property Manager notified');
      setMyViolations((prev) => [data, ...prev]);
      setForm({ violation_type: '', property_id: '', unit_number: '', building_number: '', description: '', photos: [] });
      setAiSuggestion(null);
      setLowConfNote('');
      setLowConfSubmitted(false);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const selectedPolicy = NECS_VIOLATION_POLICY.find((v) => v.key === form.violation_type);

  return (
    <div className="space-y-4" data-testid="agent-violation-form-wrap">
      <Card className="bg-zinc-900 border border-amber-600/40 p-5" data-testid="agent-violation-form">
        <div className="flex items-center gap-2 mb-3">
          <AlertTriangle className="h-5 w-5 text-amber-400" />
          <h3 className="text-white font-bold">File a Tenant Violation</h3>
          <Badge className="bg-amber-700 text-white text-[10px]">Per NECS Policy</Badge>
        </div>

        <div className="space-y-3">
          {(() => {
            // Iter-91 · Category locked while low-conf gate is unresolved.
            const categoryLocked = !!(aiSuggestion?.low_confidence && !lowConfSubmitted);
            return (
          <div>
            <Label className="text-zinc-300 text-xs">
              Violation type * {categoryLocked && <span className="text-amber-400 text-[10px] ml-1">🔒 unlocks after low-confidence note</span>}
            </Label>
            <Select
              value={form.violation_type}
              onValueChange={(v) => setForm({ ...form, violation_type: v })}
              disabled={categoryLocked}
            >
              <SelectTrigger
                className="bg-zinc-950 border-zinc-700 text-white disabled:opacity-50"
                data-testid="agent-violation-type-select"
              >
                <SelectValue placeholder={categoryLocked ? 'Locked — provide a 2-word note first' : 'Choose one of the 6 NECS violations'} />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-700 text-white">
                {NECS_VIOLATION_POLICY.map((v) => (
                  <SelectItem key={v.key} value={v.key} data-testid={`agent-violation-option-${v.key}`}>
                    {v.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedPolicy && !categoryLocked && (
              <p className="text-[11px] text-amber-300 mt-1.5">
                {selectedPolicy.tenant_facing}
              </p>
            )}
          </div>
            );
          })()}

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-zinc-300 text-xs">Property *</Label>
              <Select
                value={form.property_id}
                onValueChange={(v) => setForm({ ...form, property_id: v })}
              >
                <SelectTrigger className="bg-zinc-950 border-zinc-700 text-white" data-testid="agent-violation-property-select">
                  <SelectValue placeholder="Choose property" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-700 text-white max-h-72">
                  {properties.map((p) => (
                    <SelectItem key={p.property_id} value={p.property_id}>
                      {p.property_name || p.name || p.property_id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-zinc-300 text-xs">Unit # *</Label>
              <Input
                value={form.unit_number}
                onChange={(e) => setForm({ ...form, unit_number: e.target.value })}
                placeholder="e.g. 101"
                className="bg-zinc-950 border-zinc-700 text-white"
                data-testid="agent-violation-unit"
              />
            </div>
            <div>
              <Label className="text-zinc-300 text-xs">Building #</Label>
              <Input
                value={form.building_number}
                onChange={(e) => setForm({ ...form, building_number: e.target.value })}
                placeholder="optional"
                className="bg-zinc-950 border-zinc-700 text-white"
                data-testid="agent-violation-building"
              />
            </div>
          </div>

          <div>
            <Label className="text-zinc-300 text-xs">Notes (optional)</Label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Any extra context that helps the property manager review"
              className="bg-zinc-950 border-zinc-700 text-white min-h-[72px]"
              data-testid="agent-violation-notes"
            />
          </div>

          <div>
            <Label className="text-zinc-300 text-xs">Photo evidence * ({form.photos.length})</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              {form.photos.map((p, i) => (
                <div key={i} className="relative">
                  <img src={p.data} alt={p.name} className="w-20 h-20 object-cover rounded border border-zinc-700" />
                  <button
                    type="button"
                    onClick={() => removePhoto(i)}
                    className="absolute -top-1 -right-1 bg-red-600 text-white rounded-full p-0.5"
                    aria-label="Remove photo"
                    data-testid={`agent-violation-remove-photo-${i}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
              <label
                htmlFor="agent-violation-photo-input"
                className="w-20 h-20 flex flex-col items-center justify-center rounded border border-dashed border-zinc-600 text-zinc-400 cursor-pointer hover:border-amber-500 hover:text-amber-400"
                data-testid="agent-violation-photo-add"
              >
                <Camera className="h-5 w-5" />
                <span className="text-[10px] mt-1">Add photo</span>
              </label>
              <input
                id="agent-violation-photo-input"
                type="file"
                accept="image/*"
                capture="environment"
                onChange={addPhoto}
                className="hidden"
              />
            </div>
            {/* Iter-89 · Gemini Vision auto-classify badge. Sits directly
                under the photo grid so the agent's eye moves photo → suggestion
                → dropdown in one vertical scan. Confidence bar + rationale
                add trust; Accept collapses the pick into one tap. */}
            {aiLoading && (
              <div className="mt-2 flex items-center gap-2 text-[11px] text-zinc-400" data-testid="agent-violation-ai-loading">
                <Sparkles className="h-3.5 w-3.5 animate-pulse text-amber-400" />
                Analyzing photo…
              </div>
            )}
            {aiSuggestion && !aiLoading && (
              <div
                className="mt-2 p-2.5 rounded-lg border"
                style={{
                  borderColor: aiSuggestion.low_confidence ? 'rgba(239,68,68,0.5)' : 'rgba(201,169,98,0.5)',
                  backgroundColor: aiSuggestion.low_confidence ? 'rgba(239,68,68,0.08)' : 'rgba(201,169,98,0.08)',
                }}
                data-testid="agent-violation-ai-suggestion"
              >
                <div className="flex items-center justify-between gap-2 mb-1">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <Sparkles className={`h-3.5 w-3.5 shrink-0 ${aiSuggestion.low_confidence ? 'text-red-400' : 'text-amber-400'}`} />
                    <span className={`text-[10px] uppercase tracking-widest font-bold ${aiSuggestion.low_confidence ? 'text-red-400' : 'text-amber-400'}`}>
                      {aiSuggestion.low_confidence ? 'AI Unsure' : 'AI Suggested'}
                    </span>
                    {!aiSuggestion.low_confidence && (
                      <span className="text-xs text-white font-semibold truncate">{aiSuggestion.label}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <div className="w-14 h-1.5 rounded-full bg-zinc-800 overflow-hidden" data-testid="agent-violation-ai-confidence-bar">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${Math.round(aiSuggestion.confidence * 100)}%`,
                          backgroundColor: aiSuggestion.confidence >= 0.75 ? '#10b981' : aiSuggestion.confidence >= 0.5 ? '#f59e0b' : '#ef4444',
                        }}
                      />
                    </div>
                    <span className="text-[10px] text-zinc-400 font-mono w-8 text-right">
                      {Math.round(aiSuggestion.confidence * 100)}%
                    </span>
                  </div>
                </div>
                {aiSuggestion.rationale && (
                  <p className="text-[11px] text-zinc-400 italic mb-1.5" data-testid="agent-violation-ai-rationale">
                    “{aiSuggestion.rationale}”
                  </p>
                )}
                {aiSuggestion.low_confidence ? (
                  /* Iter-91 · Low-confidence gate. Blocks category selection
                     until agent provides a 2-word description. */
                  <div className="space-y-1.5" data-testid="agent-violation-low-conf-gate">
                    <p className="text-[12px] text-red-300 font-semibold">
                      AI confidence is low on this image. In 2 words, what do you see?
                    </p>
                    <div className="flex gap-2">
                      <Input
                        value={lowConfNote}
                        disabled={lowConfSubmitted}
                        onChange={(e) => setLowConfNote(e.target.value)}
                        maxLength={40}
                        placeholder="e.g. leaking bag"
                        className="bg-zinc-900 border-zinc-700 text-white text-xs flex-1"
                        data-testid="agent-violation-low-conf-input"
                      />
                      <Button
                        type="button"
                        size="sm"
                        disabled={lowConfSubmitted || lowConfNote.trim().split(/\s+/).length < 1 || lowConfNote.trim().length < 3}
                        onClick={() => {
                          const words = lowConfNote.trim().split(/\s+/).filter(Boolean);
                          if (words.length === 0 || words.length > 4) {
                            toast.error('Enter 1–4 words describing what you see');
                            return;
                          }
                          setLowConfSubmitted(true);
                          toast.success('Note saved · category dropdown unlocked');
                        }}
                        className="h-9 px-3 text-xs font-bold"
                        style={{
                          backgroundColor: lowConfSubmitted ? '#10b981' : '#C9A962',
                          color: '#1a1a1a',
                        }}
                        data-testid="agent-violation-low-conf-submit"
                      >
                        {lowConfSubmitted ? '✓ Saved' : 'Save note'}
                      </Button>
                    </div>
                    {lowConfSubmitted && (
                      <p className="text-[10px] text-emerald-400">
                        Category dropdown unlocked. Pick the closest match — your note is training data.
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      onClick={acceptSuggestion}
                      className="h-7 px-2.5 text-[11px] font-bold"
                      style={{
                        backgroundColor: form.violation_type === aiSuggestion.key ? '#10b981' : '#C9A962',
                        color: '#1a1a1a',
                      }}
                      data-testid="agent-violation-ai-accept"
                    >
                      {form.violation_type === aiSuggestion.key ? '✓ Accepted' : 'Accept suggestion'}
                    </Button>
                    <span className="text-[10px] text-zinc-500">
                      or override in the dropdown above — the agent&apos;s pick is final.
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          <Button
            onClick={submit}
            disabled={saving}
            className="w-full font-bold"
            style={{ backgroundColor: '#C9A962', color: '#2D2D2D' }}
            data-testid="agent-violation-submit"
          >
            {saving ? 'Submitting…' : 'Submit Violation Report'}
          </Button>
        </div>
      </Card>

      {myViolations.length > 0 && (
        <Card className="bg-zinc-900 border-zinc-700 p-4" data-testid="agent-violation-history">
          <h4 className="text-sm font-bold text-white mb-2">My Recent Reports ({myViolations.length})</h4>
          <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
            {myViolations.slice(0, 15).map((v) => (
              <div
                key={v.violation_id || v.report_id}
                className="p-2 bg-zinc-950 rounded border border-zinc-800 flex items-center justify-between gap-2"
                data-testid={`agent-violation-row-${v.violation_id || v.report_id}`}
              >
                <div className="min-w-0">
                  <p className="text-[12px] text-white font-semibold truncate">
                    {(v.violation_type || 'violation').replace(/_/g, ' ')} · Unit {v.unit_number}
                  </p>
                  <p className="text-[10px] text-zinc-400 truncate">
                    {v.property_name} · {v.created_at ? new Date(v.created_at).toLocaleString() : ''}
                  </p>
                </div>
                <Badge className={`text-[10px] ${
                  v.status === 'active' ? 'bg-blue-600' :
                  v.status === 'resolved' ? 'bg-green-600' :
                  v.status === 'rejected' ? 'bg-red-600' : 'bg-amber-600 text-black'
                }`}>
                  {(v.status || 'pending').replace(/_/g, ' ')}
                </Badge>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
